import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { CloseButton } from "src/auth/components/CloseButton/CloseButton";
import { Dialog } from "src/components/Dialog/Dialog";
import { Button } from "src/design-system/components/Button/Button";
import { useNavigate } from "react-router";
import { searchParamsChange } from "src/utils/location/searchParamsChange";
import { setSearchParamsFeature } from "src/feature/internal/searchParams/useSearchParamsFeatures";
import { useTypedLocation } from "src/utils/hooks/useTypedLocation";
import useUser from "src/utils/hooks/useUser";
import type { UserProfile } from "src/auth/utils/user";

type FreelancerLoginProps = {
  showLoginForm: () => void;
};

export const FREELANCER_MARKER_LOGIN_COOKIE = "freelancerMarkerLogin";

export function FreelancerMarkerLogin({ showLoginForm }: FreelancerLoginProps) {
  const navigate = useNavigate();
  const location = useTypedLocation();
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(user === undefined);
  const [userData, setUserData] = useState(
    user ? getUserData(user) : undefined,
  );

  useEffect(() => {
    if (user === undefined) {
      setIsOpen(true);
    } else {
      setUserData(getUserData(user));
    }
  }, [user]);

  useEffect(() => {
    if (userData !== undefined) {
      const credentials = `${userData.email}|${userData.fullName}`;
      Cookies.set(FREELANCER_MARKER_LOGIN_COOKIE, credentials, {
        expires: 365,
      });

      const newSearchParams = setSearchParamsFeature(
        "FreelancerQA",
        "default",
        location.search,
      );
      navigate(searchParamsChange(newSearchParams, location), {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData]);

  return (
    <Dialog isOpen={isOpen} onBackdropClicked={() => setIsOpen(false)}>
      <div className="relative p-8">
        <CloseButton
          className="absolute top-2 right-2"
          onClick={() => setIsOpen(false)}
        />
        <div className="flex flex-col">
          <span className="mb-2 text-center text-heading-lg">
            Marker Freelancer Login
          </span>
          <span className="mb-6 text-center text-body-lg">
            Please authenticate your account by signing into
            <br />
            the site with a valid Rome2Rio email account.
          </span>
          <Button
            className="mx-auto"
            onPress={() => {
              showLoginForm();
              setIsOpen(false);
            }}
          >
            Sign in
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function getUserData(user: UserProfile) {
  const isRome2RioUser = user.emails?.find((entry) =>
    entry.email.includes("@rome2rio.com"),
  );

  return isRome2RioUser
    ? {
        email: isRome2RioUser.email,
        fullName: user.name,
      }
    : undefined;
}
